import { Helmet } from "react-helmet-async";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import DashboardHeader from "./CompanyComponents/DashboardHeaderRouter";
import UrduKeyboard from "../Components/UrduKeyboard";

function GeneralSale() {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [language, setLanguage] = useState('ur'); // Default to Urdu
  const [companyLanguage, setCompanyLanguage] = useState('ur');

  const handleLogout = () => {
    localStorage.removeItem("companyToken");
    localStorage.removeItem("companyData");
    toast.success(companyLanguage === 'ur' ? "کامیابی سے لاگ آؤٹ ہو گئے" : "Logged out successfully");
    navigate("/company/login");
  };

  const [ledgerParties, setLedgerParties] = useState([]);
  const [generalSales, setGeneralSales] = useState([]);
  const [isLoadingSales, setIsLoadingSales] = useState(false);
  const [editingSaleId, setEditingSaleId] = useState(null);

  const [formData, setFormData] = useState({
    customerPartyId: '',
    customerName: '',
    customerNameUrdu: '',
    amount: '',
    date: new Date().toLocaleDateString('en-CA')
  });

  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showCustomerUrduKeyboard, setShowCustomerUrduKeyboard] = useState(false);
  const customerDropdownRef = useRef(null);
  const customerSearchInputRef = useRef(null);

  // Refs for fields to handle Enter key navigation
  const fieldRefs = useRef({});

  useEffect(() => {
    fetchCompanyData();
    fetchLedgerParties();
    fetchGeneralSales();
  }, []);

  // Close customer dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
    };

    if (showCustomerDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomerDropdown]);

  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        toast.error("براہ کرم لاگ ان کریں");
        navigate("/company/login");
        return;
      }

      const response = await api.get("/company/profile", {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCompany(response.data.company);
      const companyLang = response.data.company?.language || 'ur';
      setLanguage(companyLang);
      setCompanyLanguage(companyLang);
    } catch (error) {
      console.error("Fetch company error:", error);
      toast.error("کمپنی ڈیٹا لوڈ کرنے میں ناکام");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLedgerParties = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      const response = await api.get("/ledger/parties?type=customer", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLedgerParties(response.data.parties || []);
    } catch (error) {
      console.error("Fetch ledger parties error:", error);
    }
  };

  const fetchGeneralSales = async () => {
    setIsLoadingSales(true);
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      const response = await api.get("/ledger/general-sales", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneralSales(response.data.generalSales || []);
    } catch (error) {
      console.error("Fetch general sales error:", error);
      toast.error(companyLanguage === 'ur' ? 'جنرل سیل کی فہرست لوڈ کرنے میں ناکام' : 'Failed to load general sales list');
    } finally {
      setIsLoadingSales(false);
    }
  };

  const handleEdit = (sale) => {
    // Populate form with sale data
    setEditingSaleId(sale._id);
    setFormData({
      customerPartyId: sale.partyId?._id || '',
      customerName: sale.partyId?.name || '',
      customerNameUrdu: sale.partyId?.nameUrdu || '',
      amount: sale.amount || '',
      date: sale.date ? new Date(sale.date).toLocaleDateString('en-CA') : new Date().toLocaleDateString('en-CA')
    });
    setCustomerSearchTerm('');

    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });

    toast.info(companyLanguage === 'ur' ? 'جنرل سیل کی تفصیلات فارم میں بھری گئی ہیں' : 'General sale details loaded in form');
  };

  const handleDelete = async (saleId) => {
    if (!window.confirm(companyLanguage === 'ur' ? 'کیا آپ واقعی اس جنرل سیل کو حذف کرنا چاہتے ہیں؟' : 'Are you sure you want to delete this general sale?')) {
      return;
    }

    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        toast.error(companyLanguage === 'ur' ? 'براہ کرم لاگ ان کریں' : 'Please login');
        return;
      }

      await api.delete(`/ledger/transactions/${saleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(companyLanguage === 'ur' ? 'جنرل سیل کامیابی سے حذف ہو گئی' : 'General sale deleted successfully');

      // Refresh the list
      fetchGeneralSales();
    } catch (error) {
      console.error("Delete general sale error:", error);
      toast.error(
        companyLanguage === 'ur'
          ? 'جنرل سیل حذف کرنے میں ناکام: ' + (error.response?.data?.message || error.message)
          : 'Failed to delete general sale: ' + (error.response?.data?.message || error.message)
      );
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomerSelect = (partyId) => {
    const party = ledgerParties.find(p => p._id === partyId);
    if (party) {
      setFormData(prev => ({
        ...prev,
        customerPartyId: partyId,
        customerName: party.name,
        customerNameUrdu: party.nameUrdu || party.name
      }));
      setCustomerSearchTerm('');
      setShowCustomerDropdown(false);
    } else {
      setFormData(prev => ({
        ...prev,
        customerPartyId: '',
        customerName: '',
        customerNameUrdu: ''
      }));
    }
  };

  const handleCustomerSearchInputClick = () => {
    setShowCustomerUrduKeyboard(false);
    setShowCustomerDropdown(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.customerPartyId) {
      toast.error(companyLanguage === 'ur' ? 'براہ کرم کسٹمر منتخب کریں' : 'Please select a customer');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error(companyLanguage === 'ur' ? 'براہ کرم درست رقم درج کریں' : 'Please enter a valid amount');
      return;
    }

    if (!formData.date) {
      toast.error(companyLanguage === 'ur' ? 'براہ کرم تاریخ منتخب کریں' : 'Please select a date');
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        toast.error(companyLanguage === 'ur' ? 'براہ کرم لاگ ان کریں' : 'Please login');
        navigate("/company/login");
        return;
      }

      if (editingSaleId) {
        // Update existing transaction
        await api.put(
          `/ledger/transactions/${editingSaleId}`,
          {
            date: formData.date,
            description: companyLanguage === 'ur' ? 'جنرل سیل' : 'General Sale',
            type: 'debit',
            amount: parseFloat(formData.amount)
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        toast.success(companyLanguage === 'ur' ? 'جنرل سیل کامیابی سے اپ ڈیٹ ہو گئی' : 'General sale updated successfully');
        setEditingSaleId(null);
      } else {
        // Create new ledger transaction for general sale
        // For customers, debit increases balance (they owe more)
        await api.post(
          `/ledger/parties/${formData.customerPartyId}/transactions`,
          {
            date: formData.date,
            description: companyLanguage === 'ur' ? 'جنرل سیل' : 'General Sale',
            type: 'debit', // Debit increases customer balance
            amount: parseFloat(formData.amount)
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        toast.success(companyLanguage === 'ur' ? 'جنرل سیل کامیابی سے بن گئی' : 'General sale created successfully');
      }

      // Reset form
      setFormData({
        customerPartyId: '',
        customerName: '',
        customerNameUrdu: '',
        amount: '',
        date: new Date().toLocaleDateString('en-CA')
      });
      setCustomerSearchTerm('');

      // Refresh the list
      fetchGeneralSales();
    } catch (error) {
      console.error("Create general sale error:", error);
      toast.error(
        companyLanguage === 'ur'
          ? 'جنرل سیل بنانے میں ناکام: ' + (error.response?.data?.message || error.message)
          : 'Failed to create general sale: ' + (error.response?.data?.message || error.message)
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const fieldOrder = ['date', 'customerPartyId', 'amount'];
      const currentIndex = fieldOrder.indexOf(fieldName);
      if (currentIndex < fieldOrder.length - 1) {
        const nextField = fieldOrder[currentIndex + 1];
        if (fieldRefs.current[nextField]) {
          fieldRefs.current[nextField].focus();
        }
      } else if (currentIndex === fieldOrder.length - 1) {
        // Last field, submit form
        handleSubmit(e);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">{companyLanguage === 'ur' ? 'لوڈ ہو رہا ہے...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  const isUrdu = companyLanguage === 'ur';

  return (
    <div className={`min-h-screen bg-gray-50 ${isUrdu ? 'font-urdu' : ''}`} dir={isUrdu ? 'rtl' : 'ltr'}>
      <Helmet>
        <title>{isUrdu ? 'جنرل سیل' : 'General Sale'} - {company?.shopName || 'Company'}</title>
      </Helmet>

      {/* Header */}
      <DashboardHeader
        company={company}
        companyLanguage={companyLanguage}
        onLogout={handleLogout}
      />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Side - Form */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              {isUrdu ? 'جنرل سیل' : 'General Sale'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Date Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isUrdu ? 'تاریخ' : 'Date'} <span className="text-red-500">*</span>
                </label>
                <input
                  ref={(el) => { fieldRefs.current['date'] = el; }}
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, 'date')}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Customer Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isUrdu ? 'کسٹمر منتخب کریں' : 'Select Customer'} <span className="text-red-500">*</span>
                </label>
                <div className="relative" ref={customerDropdownRef}>
                  <input
                    type="text"
                    value={formData.customerPartyId ? (ledgerParties.find(p => p._id === formData.customerPartyId)?.nameUrdu || ledgerParties.find(p => p._id === formData.customerPartyId)?.name || '') : customerSearchTerm}
                    onChange={(e) => {
                      setCustomerSearchTerm(e.target.value);
                      setShowCustomerDropdown(true);
                      if (!e.target.value) {
                        handleCustomerSelect('');
                      }
                    }}
                    onClick={handleCustomerSearchInputClick}
                    onFocus={() => setShowCustomerDropdown(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !showCustomerDropdown) {
                        handleKeyDown(e, 'customerPartyId');
                      }
                    }}
                    ref={(el) => { fieldRefs.current['customerPartyId'] = el; }}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={isUrdu ? "کسٹمر تلاش کریں..." : "Search customer..."}
                    style={isUrdu ? {
                      fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif",
                      lineHeight: '1.8',
                      paddingTop: '12px',
                      paddingBottom: '12px',
                      fontSize: '13px',
                      height: 'auto',
                      minHeight: '44px',
                      verticalAlign: 'middle',
                      overflow: 'visible'
                    } : {}}
                  />
                  {showCustomerDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto" dir="rtl">
                      {ledgerParties
                        .filter(party => {
                          const searchLower = customerSearchTerm.toLowerCase();
                          const nameLower = (party.name || '').toLowerCase();
                          const nameUrduLower = (party.nameUrdu || '').toLowerCase();
                          return nameLower.includes(searchLower) || nameUrduLower.includes(searchLower);
                        })
                        .map(party => (
                          <div
                            key={party._id}
                            onClick={() => {
                              handleCustomerSelect(party._id);
                              setCustomerSearchTerm('');
                              setShowCustomerDropdown(false);
                            }}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-gray-700 text-right border-b border-gray-100 last:border-b-0"
                          >
                            {party.nameUrdu || party.name}
                          </div>
                        ))}
                      {ledgerParties.filter(party => {
                        const searchLower = customerSearchTerm.toLowerCase();
                        const nameLower = (party.name || '').toLowerCase();
                        const nameUrduLower = (party.nameUrdu || '').toLowerCase();
                        return nameLower.includes(searchLower) || nameUrduLower.includes(searchLower);
                      }).length === 0 && customerSearchTerm && (
                          <div className="px-3 py-2 text-gray-500 text-center">
                            {isUrdu ? 'کوئی کسٹمر نہیں ملا' : 'No customer found'}
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>

              {/* Amount Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isUrdu ? 'رقم' : 'Amount'} <span className="text-red-500">*</span>
                </label>
                <input
                  ref={(el) => { fieldRefs.current['amount'] = el; }}
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, 'amount')}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-gray-700 text-right focus:outline-none focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder={isUrdu ? "رقم درج کریں" : "Enter amount"}
                  required
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-4" style={isUrdu ? { flexDirection: 'row-reverse' } : {}}>
                <button
                  type="button"
                  onClick={() => navigate('/company/dashboard')}
                  className="px-6 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  {isUrdu ? 'منسوخ' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving
                    ? (isUrdu ? 'محفوظ ہو رہا ہے...' : 'Saving...')
                    : editingSaleId
                      ? (isUrdu ? 'اپ ڈیٹ کریں' : 'Update')
                      : (isUrdu ? 'محفوظ کریں' : 'Save')
                  }
                </button>
                {editingSaleId && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingSaleId(null);
                      setFormData({
                        customerPartyId: '',
                        customerName: '',
                        customerNameUrdu: '',
                        amount: '',
                        date: new Date().toLocaleDateString('en-CA')
                      });
                      setCustomerSearchTerm('');
                    }}
                    className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    {isUrdu ? 'منسوخ' : 'Cancel Edit'}
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Right Side - General Sales List */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">
              {isUrdu ? 'جنرل سیل کی فہرست' : 'General Sales List'}
            </h3>

            {isLoadingSales ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-4 text-gray-600">{isUrdu ? 'لوڈ ہو رہا ہے...' : 'Loading...'}</p>
              </div>
            ) : generalSales.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {isUrdu ? 'کوئی جنرل سیل نہیں ملی' : 'No general sales found'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse border border-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 border border-gray-300 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                        {isUrdu ? 'تاریخ' : 'Date'}
                      </th>
                      <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 border border-gray-300 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                        {isUrdu ? 'کسٹمر' : 'Customer'}
                      </th>
                      <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 border border-gray-300 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                        {isUrdu ? 'رقم' : 'Amount'}
                      </th>
                      <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 border border-gray-300 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                        {isUrdu ? 'عمل' : 'Actions'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {generalSales.map((sale) => (
                      <tr key={sale._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right border border-gray-300">
                          {sale.date ? new Date(sale.date).toLocaleDateString('en-CA') : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right border border-gray-300">
                          {sale.partyId?.nameUrdu || sale.partyId?.name || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 text-right border border-gray-300">
                          {parseFloat(sale.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right border border-gray-300">
                          <div className="flex items-center gap-2 justify-end" style={isUrdu ? { flexDirection: 'row-reverse' } : {}}>
                            <button
                              onClick={() => handleEdit(sale)}
                              className="px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs transition-colors"
                              title={isUrdu ? 'تبدیل کریں' : 'Edit'}
                            >
                              {isUrdu ? 'تبدیل کریں' : 'Edit'}
                            </button>
                            <button
                              onClick={() => handleDelete(sale._id)}
                              className="px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 text-xs transition-colors"
                              title={isUrdu ? 'ختم کریں' : 'Delete'}
                            >
                              {isUrdu ? 'ختم کریں' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="3" className={`px-4 py-3 text-sm font-bold text-gray-700 text-right border border-gray-300 ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'کل رقم' : 'Total Amount'}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right border border-gray-300">
                        {generalSales.reduce((sum, sale) => sum + (parseFloat(sale.amount) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GeneralSale;

